import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import {
  FiSearch,
  FiFilter,
  FiEye,
  FiPackage,
  FiCalendar,
  FiMapPin,
  FiPhone,
  FiMail,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiUserCheck,
  FiDownload
} from 'react-icons/fi';
import { GiSheep } from 'react-icons/gi';
import { AiOutlineClose } from 'react-icons/ai';
import displayError from '../../utils/ErrorHandler';
import { useContext } from 'react';
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import authenticate from '../../utils/Authenticate';
import { isAdminRole, isSilatViewerRole } from '../../utils/roles';
import Toast from './Toast';

const CommitmentsDisplay = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('items'); // 'items' or 'zabihat'
  
  // Item commitments state
  const [commitments, setCommitments] = useState([]);
  const [filteredCommitments, setFilteredCommitments] = useState([]);
  
  // Zabihat commitments state
  const [zabihatCommitments, setZabihatCommitments] = useState([]);
  const [filteredZabihatCommitments, setFilteredZabihatCommitments] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states for items
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jamiatFilter, setJamiatFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Filter states for zabihat
  const [zabihatSearchTerm, setZabihatSearchTerm] = useState('');
  const [zabihatStatusFilter, setZabihatStatusFilter] = useState('all');
  const [zabihatJamiatFilter, setZabihatJamiatFilter] = useState('all');
  const [zabihatDateFilter, setZabihatDateFilter] = useState('all');
  
  // View states
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [selectedCommitment, setSelectedCommitment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Unique values for filters
  const [uniqueJamiats, setUniqueJamiats] = useState([]);
  const [uniqueItems, setUniqueItems] = useState([]);
  const [uniqueZabihatJamiats, setUniqueZabihatJamiats] = useState([]);

  const [isEditingQty, setIsEditingQty] = useState(false);
  const [quantity, setQuantity] = useState(selectedCommitment?.quantity || 0);
    // toast state
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [updatingQty, setUpdatingQty] = useState(false);
  const [deletingCommitment, setDeletingCommitment] = useState(false);

  const {user,setUser} = useContext(UserContext);

  // Silat Viewer is read-only: can view/export, but must never see edit/approve controls.
  const canEdit = !isSilatViewerRole(user?.role);
  // Deleting a commitment (any status, either tab) is an admin-only action.
  const canDelete = isAdminRole(user?.role);

  const navigate = useNavigate();

  // 🛡️ Auth check
  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, setUser, navigate]);

  useEffect(() => {
    fetchCommitments();
    fetchZabihatCommitments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [commitments, searchTerm, statusFilter, jamiatFilter, itemFilter, dateFilter]);

  useEffect(() => {
    applyZabihatFilters();
  }, [zabihatCommitments, zabihatSearchTerm, zabihatStatusFilter, zabihatJamiatFilter, zabihatDateFilter]);

  const fetchCommitments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/commit/getAll`,{ withCredentials: true });
      console.log(response);
      if (response.data.success) {
        const commitmentsData = response.data.data;
        setCommitments(commitmentsData);
        
        // Extract unique values for filters
        const jamiats = [...new Set(commitmentsData.map(c => c.jamiat?.jamiat_name).filter(Boolean))];
        const items = [...new Set(commitmentsData.map(c => c.silatItem?.name).filter(Boolean))];
        
        setUniqueJamiats(jamiats);
        setUniqueItems(items);
      }
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchZabihatCommitments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/zabihatCommitment/getAll`,{ withCredentials: true });
      console.log('Zabihat commitments:', response);
      if (response.data.success) {
        const zabihatCommitmentsData = response.data.data;
        setZabihatCommitments(zabihatCommitmentsData);
        
        // Extract unique jamiats for zabihat filters
        const zabihatJamiats = [...new Set(zabihatCommitmentsData.map(c => c.jamiat?.jamiat_name).filter(Boolean))];
        setUniqueZabihatJamiats(zabihatJamiats);
      }
    } catch (err) {
      console.error('Error fetching zabihat commitments:', err);
      // Don't set error here as it might be that the API doesn't exist yet
    }
  };

  const getTotalCommitmentValue = () => {
    if (activeTab === 'items') {
      return filteredCommitments.reduce((total, commitment) => {
        const itemPrice = commitment.silatItem?.price || 0;
        const quantity = commitment.quantity || 0;
        return total + (itemPrice * quantity);
      }, 0);
    } else {
      return filteredZabihatCommitments.reduce((total, commitment) => {
        const zabihatPrice = commitment.silatZabihat?.price || 0;
        const quantity = commitment.quantity || 0;
        return total + (zabihatPrice * quantity);
      }, 0);
    }
  };

  const applyFilters = () => {
    let filtered = [...commitments];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(commitment => 
        commitment.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        commitment.silatItem?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        commitment.jamiat?.jamiat_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        commitment.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(commitment => commitment.status === statusFilter);
    }

    // Jamiat filter
    if (jamiatFilter !== 'all') {
      filtered = filtered.filter(commitment => commitment.jamiat?.jamiat_name === jamiatFilter);
    }

    // Item filter
    if (itemFilter !== 'all') {
      filtered = filtered.filter(commitment => commitment.silatItem?.name === itemFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      filtered = filtered.filter(commitment => {
        const deliveryDate = new Date(commitment.estimatedDeliveryDate);
        const diffTime = deliveryDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (dateFilter) {
          case 'overdue':
            return diffDays < 0 && commitment.status !== 'delivered';
          case 'today':
            return diffDays === 0;
          case 'tomorrow':
            return diffDays === 1;
          case 'week':
            return diffDays >= 0 && diffDays <= 7;
          case 'month':
            return diffDays >= 0 && diffDays <= 30;
          default:
            return true;
        }
      });
    }

    setFilteredCommitments(filtered);
  };

  const applyZabihatFilters = () => {
    let filtered = [...zabihatCommitments];

    // Search filter
    if (zabihatSearchTerm) {
      filtered = filtered.filter(commitment => 
        commitment.contactName?.toLowerCase().includes(zabihatSearchTerm.toLowerCase()) ||
        commitment.jamiat?.jamiat_name?.toLowerCase().includes(zabihatSearchTerm.toLowerCase()) ||
        commitment.contactEmail?.toLowerCase().includes(zabihatSearchTerm.toLowerCase())
      );
    }

    // Status filter
    if (zabihatStatusFilter !== 'all') {
      filtered = filtered.filter(commitment => commitment.status === zabihatStatusFilter);
    }

    // Jamiat filter
    if (zabihatJamiatFilter !== 'all') {
      filtered = filtered.filter(commitment => commitment.jamiat?.jamiat_name === zabihatJamiatFilter);
    }

    // Date filter
    if (zabihatDateFilter !== 'all') {
      const today = new Date();
      filtered = filtered.filter(commitment => {
        const paymentDate = new Date(commitment.estimatedDeliveryDate);
        const diffTime = paymentDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        switch (zabihatDateFilter) {
          case 'overdue':
            return diffDays < 0 && commitment.status !== 'paid';
          case 'today':
            return diffDays === 0;
          case 'tomorrow':
            return diffDays === 1;
          case 'week':
            return diffDays >= 0 && diffDays <= 7;
          case 'month':
            return diffDays >= 0 && diffDays <= 30;
          default:
            return true;
        }
      });
    }

    setFilteredZabihatCommitments(filtered);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'commited':
        return <FiClock className="text-orange-500" size={16} />;
      case 'transit':
        return <FiTruck className="text-blue-500" size={16} />;
      case 'delivered':
        return <FiCheckCircle className="text-green-500" size={16} />;
      case 'paid':
        return <FiCheckCircle className="text-green-500" size={16} />;
      case 'approved':
        return <FiUserCheck className="text-green-500" size={16} />;
      default:
        return <FiAlertCircle className="text-gray-500" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'commited':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'transit':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'approved':
         return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const isOverdue = (date, status) => {
    return new Date(date) < new Date() && (status !== 'delivered' && status !== 'paid');
  };

  const handleViewDetails = (commitment) => {
    setSelectedCommitment(commitment);
    // Start each modal open on a clean slate - otherwise a qty edit left
    // open (without Confirm/Cancel) on one commitment would carry over,
    // pre-opened with a stale value, into the next one viewed.
    setIsEditingQty(false);
    setQuantity(commitment.quantity || 0);
    setShowDetailsModal(true);
  };

  const handleUpdate = async () => {
  setUpdatingQty(true);
  const formData = new FormData();
  formData.append("_id", selectedCommitment._id);
  formData.append("quantity", quantity);
  formData.append("contactName", selectedCommitment.contactName);
  formData.append("contactEmail", selectedCommitment.contactEmail);
  formData.append("contactNumber", selectedCommitment.contactNumber);
  formData.append("estimatedDeliveryDate", selectedCommitment.estimatedDeliveryDate);
  formData.append("status", selectedCommitment.status);
  formData.append("bill", selectedCommitment.ewayBill || "");

  // Item and Zabihat commitments are updated through separate backend
  // routes/models (see commitmentRouter.js: /update vs /zabihat-commit/update),
  // even though the request body shape is identical for both.
  const endpoint = activeTab === 'items'
    ? `${process.env.REACT_APP_API_URI}/api/commit/update`
    : `${process.env.REACT_APP_API_URI}/api/commit/zabihat-commit/update`;

  try {
    const res = await axios.put(
      endpoint,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      }
    );

    setSelectedCommitment(prev => ({
      ...prev,
      quantity: quantity,
    }));

    const applyQty = item => (item._id === selectedCommitment._id ? { ...item, quantity } : item);
    if (activeTab === 'items') {
      setCommitments(prev => prev.map(applyQty));
    } else {
      setZabihatCommitments(prev => prev.map(applyQty));
    }

    setIsEditingQty(false);
    console.log("Updated:", res.data);
    // show success toast
    setToast({
      message: "Quantity updated successfully!",
      type: "success",
    });
    setUpdatingQty(false)
    // setShowDetailsModal(false);
  } catch (err) {
    setToast({
      message: err.response.data.custommessage || "Failed to update quantity",
      type: "error",
    });
    console.error("Error updating:", err);
    setUpdatingQty(false);
  }
};

const handleCommitmentStatusUpdate = async () => {
    if (!selectedCommitment) return;
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URI}/api/commit/statusUpdate`,
        { _id: selectedCommitment._id, status: "approved" },
        { withCredentials: true }
      );
      setSelectedCommitment((prev) => ({
        ...prev,
        status: "approved",
      }));
      setCommitments((prev) =>
        prev.map((item) =>
          item._id === selectedCommitment._id
            ? { ...item, status: "approved" }
            : item
        )
      );
      setToast({
        message: "Commitment approved successfully!",
        type: "success",
      });
      setShowDetailsModal(false);
    } catch (err) {
      setToast({
        message:
          err.response?.data?.custommessage || "Failed to approve commitment",
        type: "error",
      });
    }
  };

  // Admin-only: delete a commitment (item or zabihat) regardless of status.
  const handleDeleteCommitment = async () => {
    if (!selectedCommitment) return;
    if (!window.confirm('Delete this commitment? This action cannot be undone.')) return;

    setDeletingCommitment(true);
    try {
      const endpoint = activeTab === 'items'
        ? `${process.env.REACT_APP_API_URI}/api/commit/delete`
        : `${process.env.REACT_APP_API_URI}/api/zabihatCommitment/delete`;

      await axios.delete(endpoint, {
        data: { commitment: { _id: selectedCommitment._id } },
        withCredentials: true,
      });

      if (activeTab === 'items') {
        setCommitments(prev => prev.filter(c => c._id !== selectedCommitment._id));
      } else {
        setZabihatCommitments(prev => prev.filter(c => c._id !== selectedCommitment._id));
      }

      setToast({ message: 'Commitment deleted successfully!', type: 'success' });
      setShowDetailsModal(false);
    } catch (err) {
      setToast({
        message: err.response?.data?.custommessage || err.response?.data?.message || 'Failed to delete commitment',
        type: 'error',
      });
    } finally {
      setDeletingCommitment(false);
    }
  };

  const clearAllFilters = () => {
    if (activeTab === 'items') {
      setSearchTerm('');
      setStatusFilter('all');
      setJamiatFilter('all');
      setItemFilter('all');
      setDateFilter('all');
    } else {
      setZabihatSearchTerm('');
      setZabihatStatusFilter('all');
      setZabihatJamiatFilter('all');
      setZabihatDateFilter('all');
    }
  };

  const getFilterSummary = () => {
    if (activeTab === 'items') {
      const total = commitments.length;
      const filtered = filteredCommitments.length;
      const committed = filteredCommitments.filter(c => c.status === 'commited').length;
      const inTransit = filteredCommitments.filter(c => c.status === 'transit').length;
      const delivered = filteredCommitments.filter(c => c.status === 'delivered').length;
      const approved = filteredCommitments.filter(c => c.status === 'approved').length;
      const overdue = filteredCommitments.filter(c => isOverdue(c.estimatedDeliveryDate, c.status)).length;

      return { total, filtered, committed, inTransit, delivered, approved, overdue };
    } else {
      const total = zabihatCommitments.length;
      const filtered = filteredZabihatCommitments.length;
      const committed = filteredZabihatCommitments.filter(c => c.status === 'commited').length;
      const paid = filteredZabihatCommitments.filter(c => c.status === 'paid').length;
      const overdue = filteredZabihatCommitments.filter(c => isOverdue(c.estimatedDeliveryDate, c.status)).length;
      const totalAmount = filteredZabihatCommitments.reduce((sum, c) => sum + (c.quantity * (c.silatZabihat?.price || 0)), 0);

      return { total, filtered, committed, paid, overdue, totalAmount };
    }
  };

  const stats = getFilterSummary();

  const exportToExcel = () => {
    const isItems = activeTab === 'items';
    const rows = isItems ? filteredCommitments : filteredZabihatCommitments;

    if (!rows.length) {
      setToast({ message: 'No commitments to export.', type: 'error' });
      return;
    }

    const headers = isItems
      ? ['Item', 'Jamiat', 'Jamaat', 'Volunteer / Individual Name', 'Contact Name', 'Contact Number',
         'Contact Email', 'Quantity', 'Unit', 'Unit Price (₹)', 'Contribution Type', 'Total Amount (₹)',
         'Status', 'Expected Delivery', 'Delivered On', 'E-Way Bill', 'Created On']
      : ['Jamiat', 'Jamaat', 'Contact Name', 'Contact Number', 'Contact Email', 'Quantity',
         'Unit Price (₹)', 'Total Amount (₹)', 'Status', 'Payment Due', 'Paid On', 'Created On'];

    const statusColIdx = isItems ? 12 : 8;

    const dataRows = rows.map(c => {
      if (isItems) {
        const unitPrice = c.silatItem?.price || 0;
        return [
          c.silatItem?.name || 'Unknown',
          c.jamiat?.jamiat_name || (c.individualName ? 'Personal/Individual' : ''),
          c.jamiat?.jamaat_name || '',
          c.volunteerName || c.individualName || '',
          c.contactName || '',
          c.contactNumber || '',
          c.contactEmail || '',
          c.quantity || 0,
          c.silatItem?.unit || '',
          unitPrice,
          c.contributionType === 'cash' ? 'Cash' : 'In Kind',
          (c.quantity || 0) * unitPrice,
          c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : '',
          c.estimatedDeliveryDate ? formatDate(c.estimatedDeliveryDate) : '',
          c.deliveryDate ? formatDate(c.deliveryDate) : '',
          c.ewayBill || '',
          c.createdAt ? formatDate(c.createdAt) : '',
        ];
      }
      const unitPrice = c.silatZabihat?.price || 0;
      return [
        c.jamiat?.jamiat_name || (c.individualName ? 'Personal/Individual' : ''),
        c.jamiat?.jamaat_name || '',
        c.contactName || '',
        c.contactNumber || '',
        c.contactEmail || '',
        c.quantity || 0,
        unitPrice,
        (c.quantity || 0) * unitPrice,
        c.status ? c.status.charAt(0).toUpperCase() + c.status.slice(1) : '',
        c.estimatedDeliveryDate ? formatDate(c.estimatedDeliveryDate) : '',
        c.paymentDate ? formatDate(c.paymentDate) : '',
        c.createdAt ? formatDate(c.createdAt) : '',
      ];
    });

    const titleText = `${isItems ? 'Item' : 'Zabihat'} Commitments — Exported ${formatDate(new Date())} (${rows.length} of ${isItems ? commitments.length : zabihatCommitments.length})`;
    const headerRowIdx = 2; // title row (0), blank row (1), header row (2)
    const aoa = [[titleText], [], headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];

    const titleStyle = {
      font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '1F4E78' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '2F75B5' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'D9D9D9' } },
        bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
        left: { style: 'thin', color: { rgb: 'D9D9D9' } },
        right: { style: 'thin', color: { rgb: 'D9D9D9' } },
      },
    };
    const thinGreyBorder = {
      top: { style: 'thin', color: { rgb: 'E5E7EB' } },
      bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
      left: { style: 'thin', color: { rgb: 'E5E7EB' } },
      right: { style: 'thin', color: { rgb: 'E5E7EB' } },
    };
    const evenRowStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'F2F7FC' } }, border: thinGreyBorder };
    const oddRowStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFF' } }, border: thinGreyBorder };

    const statusColors = {
      commited: 'FDE9CE',
      approved: 'D9F2D9',
      transit: 'D6E9FB',
      delivered: 'D9F2D9',
      paid: 'D9F2D9',
      received: 'D9F2D9',
      'partial-received': 'FFF3CD',
    };

    if (ws['A1']) ws['A1'].s = titleStyle;

    headers.forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIdx, c: colIdx });
      if (ws[cellRef]) ws[cellRef].s = headerStyle;
    });

    dataRows.forEach((row, rIdx) => {
      const excelRow = headerRowIdx + 1 + rIdx;
      const baseStyle = rIdx % 2 === 0 ? oddRowStyle : evenRowStyle;
      row.forEach((_, cIdx) => {
        const cellRef = XLSX.utils.encode_cell({ r: excelRow, c: cIdx });
        if (!ws[cellRef]) return;
        let style = baseStyle;
        if (cIdx === statusColIdx) {
          const rawStatus = rows[rIdx].status || '';
          style = {
            ...baseStyle,
            fill: { patternType: 'solid', fgColor: { rgb: statusColors[rawStatus] || baseStyle.fill.fgColor.rgb } },
            font: { bold: true },
            alignment: { horizontal: 'center' },
          };
        }
        ws[cellRef].s = style;
      });
    });

    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: headerRowIdx, c: 0 }, e: { r: headerRowIdx, c: headers.length - 1 } }) };
    ws['!rows'] = [{ hpt: 22 }, { hpt: 6 }, { hpt: 20 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isItems ? 'Item Commitments' : 'Zabihat Commitments');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const fileName = `${isItems ? 'Item' : 'Zabihat'}_Commitments_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setToast({ message: `Exported ${rows.length} commitment${rows.length === 1 ? '' : 's'} to Excel.`, type: 'success' });
  };

  const renderItemsContent = () => {
    const currentCommitments = filteredCommitments;
    const currentSearchTerm = searchTerm;
    const currentStatusFilter = statusFilter;
    const currentJamiatFilter = jamiatFilter;
    const currentItemFilter = itemFilter;
    const currentDateFilter = dateFilter;

    return (
      <>
        <div className="bg-gradient-to-r from-fmb to-blue-600 text-white p-6 rounded-lg mb-6">
        <div className="text-center">
          <div className="text-3xl font-bold">{formatCurrency(getTotalCommitmentValue())}</div>
          <div className="text-sm opacity-90">
            Total Value of {activeTab === 'items' ? 'Item' : 'Zabihat'} Commitments
            {stats.filtered !== stats.total && ` (${stats.filtered} of ${stats.total} shown)`}
          </div>
        </div>
      </div>
        {/* Search and Filters for Items */}
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by contact name, item, jamiat, or email..."
              value={currentSearchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            />
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <select
              value={currentStatusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Status</option>
              <option value="commited">Committed</option>
              <option value="transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="approved">Approved</option>
            </select>

            <select
              value={currentJamiatFilter}
              onChange={(e) => setJamiatFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Jamiats</option>
              {uniqueJamiats.map(jamiat => (
                <option key={jamiat} value={jamiat}>{jamiat}</option>
              ))}
            </select>

            <select
              value={currentItemFilter}
              onChange={(e) => setItemFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Items</option>
              {uniqueItems.map(item => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>

            <select
              value={currentDateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Dates</option>
              <option value="overdue">Overdue</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>

            <button
              onClick={clearAllFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>

            <div className="flex items-center justify-center">
              <FiFilter className="text-gray-400 mr-2" size={16} />
              <span className="text-sm text-gray-600">{stats.filtered} of {stats.total}</span>
            </div>
          </div>
        </div>

        {/* Items Content */}
        {currentCommitments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <FiPackage className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No item commitments found</h3>
            <p className="text-gray-500 mb-4">
              {commitments.length === 0 
                ? "No item commitments have been created yet." 
                : "Try adjusting your filters to see more results."
              }
            </p>
            {commitments.length > 0 && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-fmb text-white rounded-lg hover:bg-fmb-dark transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentCommitments.map(commitment => (
                  <div key={commitment._id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-fmb text-lg mb-1">
                            {commitment.silatItem?.name || 'Unknown Item'}
                          </h3>
                          <p className="text-sm text-gray-600 flex items-center">
                            <FiMapPin size={14} className="mr-1" />
                            {(commitment.jamiat && `${commitment.jamiat?.jamaat_name} -  ${commitment.jamiat?.jamiat_name}`) || `Personal/Individual - ${commitment?.individualName}`}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(commitment.status)}`}>
                          {getStatusIcon(commitment.status)}
                          {commitment.status.charAt(0).toUpperCase() + commitment.status.slice(1)}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Quantity:</span>
                          <span className="font-medium">{commitment.quantity} {commitment.silatItem?.unit}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Contact:</span>
                          <span className="font-medium">{commitment.contactName}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Expected:</span>
                          <span className={`font-medium ${isOverdue(commitment.estimatedDeliveryDate, commitment.status) ? 'text-red-600' : ''}`}>
                            {formatDate(commitment.estimatedDeliveryDate)}
                            {isOverdue(commitment.estimatedDeliveryDate, commitment.status) && (
                              <span className="ml-1 text-red-500">⚠️</span>
                            )}
                          </span>
                        </div>
                        {commitment.deliveryDate && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Delivered:</span>
                            <span className="font-medium text-green-600">{formatDate(commitment.deliveryDate)}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <button
                        onClick={() => handleViewDetails(commitment)}
                        className="w-full px-4 py-2 bg-fmb text-white rounded-lg hover:bg-fmb-dark transition-colors flex items-center justify-center gap-2"
                      >
                        <FiEye size={16} />
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jamiat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jamaat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentCommitments.map(commitment => (
                        <tr key={commitment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{commitment.silatItem?.name || 'Unknown'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.jamiat?.jamiat_name || 'Unknown'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.jamiat?.jamaat_name || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.contactName}</div>
                            <div className="text-sm text-gray-500">{commitment.contactNumber}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {commitment.quantity} {commitment.silatItem?.unit}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 w-fit ${getStatusColor(commitment.status)}`}>
                              {getStatusIcon(commitment.status)}
                              {commitment.status.charAt(0).toUpperCase() + commitment.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isOverdue(commitment.estimatedDeliveryDate, commitment.status) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                              {formatDate(commitment.estimatedDeliveryDate)}
                              {isOverdue(commitment.estimatedDeliveryDate, commitment.status) && (
                                <span className="ml-1 text-red-500">⚠️</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleViewDetails(commitment)}
                              className="text-fmb hover:text-fmb-dark transition-colors flex items-center gap-1"
                            >
                              <FiEye size={16} />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  const renderZabihatContent = () => {
    const currentCommitments = filteredZabihatCommitments;
    const currentSearchTerm = zabihatSearchTerm;
    const currentStatusFilter = zabihatStatusFilter;
    const currentJamiatFilter = zabihatJamiatFilter;
    const currentDateFilter = zabihatDateFilter;

    return (
      <>
        {/* Search and Filters for Zabihat */}
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by contact name, jamiat, or email..."
              value={currentSearchTerm}
              onChange={(e) => setZabihatSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            />
          </div>

          {/* Filter Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <select
              value={currentStatusFilter}
              onChange={(e) => setZabihatStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Status</option>
              <option value="commited">Committed</option>
              <option value="paid">Paid</option>
            </select>

            <select
              value={currentJamiatFilter}
              onChange={(e) => setZabihatJamiatFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Jamiats</option>
              {uniqueZabihatJamiats.map(jamiat => (
                <option key={jamiat} value={jamiat}>{jamiat}</option>
              ))}
            </select>

            <select
              value={currentDateFilter}
              onChange={(e) => setZabihatDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
            >
              <option value="all">All Dates</option>
              <option value="overdue">Overdue</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>

            <button
              onClick={clearAllFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>

            <div className="flex items-center justify-center">
              <FiFilter className="text-gray-400 mr-2" size={16} />
              <span className="text-sm text-gray-600">{stats.filtered} of {stats.total}</span>
            </div>
          </div>
        </div>

        {/* Zabihat Content */}
        {currentCommitments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <GiSheep className="mx-auto mb-4 text-gray-400" size={48} />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No zabihat commitments found</h3>
            <p className="text-gray-500 mb-4">
              {zabihatCommitments.length === 0 
                ? "No zabihat commitments have been created yet." 
                : "Try adjusting your filters to see more results."
              }
            </p>
            {zabihatCommitments.length > 0 && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-fmb text-white rounded-lg hover:bg-fmb-dark transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            {viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentCommitments.map(commitment => (
                  <div key={commitment._id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-fmb text-lg mb-1 flex items-center">
                            <GiSheep size={18} className="mr-2" />
                            Zabihat
                          </h3>
                          <p className="text-sm text-gray-600 flex items-center">
                            <FiMapPin size={14} className="mr-1" />
                            {`${commitment.jamiat?.jamaat_name} -  ${commitment.jamiat?.jamiat_name}` || 'Personal/Individual'}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(commitment.status)}`}>
                          {getStatusIcon(commitment.status)}
                          {commitment.status.charAt(0).toUpperCase() + commitment.status.slice(1)}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Quantity:</span>
                          <span className="font-medium">{commitment.quantity}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Price per unit:</span>
                          <span className="font-medium">{formatCurrency(commitment.silatZabihat?.price || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Amount:</span>
                          <span className="font-semibold text-fmb">{formatCurrency((commitment.quantity * (commitment.silatZabihat?.price || 0)))}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Contact:</span>
                          <span className="font-medium">{commitment.contactName}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Payment Due:</span>
                          <span className={`font-medium ${isOverdue(commitment.estimatedDeliveryDate, commitment.status) ? 'text-red-600' : ''}`}>
                            {formatDate(commitment.estimatedDeliveryDate)}
                            {isOverdue(commitment.estimatedDeliveryDate, commitment.status) && (
                              <span className="ml-1 text-red-500">⚠️</span>
                            )}
                          </span>
                        </div>
                        {commitment.paymentDate && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Paid on:</span>
                            <span className="font-medium text-green-600">{formatDate(commitment.paymentDate)}</span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <button
                        onClick={() => handleViewDetails(commitment)}
                        className="w-full px-4 py-2 bg-fmb text-white rounded-lg hover:bg-fmb-dark transition-colors flex items-center justify-center gap-2"
                      >
                        <FiEye size={16} />
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jamiat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jamaat</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Due</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentCommitments.map(commitment => (
                        <tr key={commitment._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.jamiat?.jamiat_name || 'Unknown'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.jamiat?.jamaat_name || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{commitment.contactName}</div>
                            <div className="text-sm text-gray-500">{commitment.contactNumber}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{commitment.quantity}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(commitment.silatZabihat?.price || 0)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-fmb">{formatCurrency((commitment.quantity * (commitment.silatZabihat?.price || 0)))}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 w-fit ${getStatusColor(commitment.status)}`}>
                              {getStatusIcon(commitment.status)}
                              {commitment.status.charAt(0).toUpperCase() + commitment.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm ${isOverdue(commitment.estimatedDeliveryDate, commitment.status) ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                              {formatDate(commitment.estimatedDeliveryDate)}
                              {isOverdue(commitment.estimatedDeliveryDate, commitment.status) && (
                                <span className="ml-1 text-red-500">⚠️</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleViewDetails(commitment)}
                              className="text-fmb hover:text-fmb-dark transition-colors flex items-center gap-1"
                            >
                              <FiEye size={16} />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[70vh] w-full">
        <div className="text-fmb">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fmb mx-auto mb-4"></div>
          <p>Loading commitments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-fmb">Commitments Dashboard</h1>
            <p className="text-gray-600">Track and manage all commitments</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              className="px-4 py-2 rounded-md transition-colors bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
              title="Export the currently filtered commitments to Excel"
            >
              <FiDownload size={16} />
              Export to Excel
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'cards' 
                  ? 'bg-fmb text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-4 py-2 rounded-md transition-colors ${
                viewMode === 'table' 
                  ? 'bg-fmb text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Table
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('items')}
            className={`px-6 py-2 rounded-md transition-colors font-medium ${
              activeTab === 'items'
                ? 'bg-white text-fmb shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiPackage size={16} />
              Item Commitments
            </div>
          </button>
          <button
            onClick={() => setActiveTab('zabihat')}
            className={`px-6 py-2 rounded-md transition-colors font-medium ${
              activeTab === 'zabihat'
                ? 'bg-white text-fmb shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <GiSheep size={16} />
              Zabihat Commitments
            </div>
          </button>
        </div>

        {/* Stats Summary */}
        {activeTab === 'items' ? (
          <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.filtered}</div>
              <div className="text-xs text-gray-500">Filtered</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.committed}</div>
              <div className="text-xs text-orange-600">Committed</div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.inTransit}</div>
              <div className="text-xs text-blue-600">In Transit</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{stats.delivered}</div>
              <div className="text-xs text-green-600">Delivered</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.approved}</div>
              <div className="text-xs text-yellow-600">Approved</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
              <div className="text-xs text-red-600">Overdue</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.filtered}</div>
              <div className="text-xs text-gray-500">Filtered</div>
            </div>
            <div className="bg-orange-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">{stats.committed}</div>
              <div className="text-xs text-orange-600">Committed</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
              <div className="text-xs text-green-600">Paid</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
              <div className="text-xs text-red-600">Overdue</div>
            </div>
          </div>
        )}

        {/* Total Amount for Zabihat */}
        {activeTab === 'zabihat' && (
          <div className="bg-gradient-to-r from-fmb to-blue-600 text-white p-4 rounded-lg mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold">{formatCurrency(stats.totalAmount || 0)}</div>
              <div className="text-sm opacity-90">Total Commitment Value</div>
            </div>
          </div>
        )}

        {/* Render appropriate content based on active tab */}
        {activeTab === 'items' ? renderItemsContent() : renderZabihatContent()}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedCommitment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-fmb text-white p-4 rounded-t-lg">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {activeTab === 'items' ? 'Item Commitment Details' : 'Zabihat Commitment Details'}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  <AiOutlineClose size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Item/Zabihat Information */}
              <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-semibold text-fmb mb-5">
                  {activeTab === 'items' ? 'Item Information' : 'Zabihat Information'}
                </h3>
              
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {activeTab === 'items' ? (
                    <>
                      {/* Item Name */}
                      <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Item Name
                        </label>
                        <p className="text-fmb font-semibold text-lg">
                          {selectedCommitment.silatItem?.name}
                        </p>
                      </div>

                    {/* Quantity Committed Card */}
                    <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                      <label className="block text-sm font-medium text-gray-600 mb-2">
                        Quantity Committed
                      </label>
                      <div className="flex flex-col gap-2 text-gray-800">
                        <div className="flex justify-between items-start">
                          <span>Quantity:</span>
                    
                          {!isEditingQty ? (
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">
                                {selectedCommitment.quantity} {selectedCommitment.silatItem?.unit}
                              </span>
                              {canEdit && (
                                <button
                                  onClick={() => setIsEditingQty(true)}
                                  className="text-blue-600 text-sm font-medium hover:underline"
                                >
                                  Edit Qty
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-2">
                              <input
                                type="number"
                                // value={selectedCommitment.quantity}
                                disabled={updatingQty}
                                placeholder={selectedCommitment.quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                className="w-24 border rounded-lg p-1 text-right"
                              />
                              <div className='flex items-center gap-2'>
                                <button
                                onClick={handleUpdate}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingQty(false);
                                  setQuantity(selectedCommitment.quantity);
                                }}
                                className="px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                              </div>
                              
                            </div>
                          )}
                        </div>
                    
                        <div className="flex justify-between">
                          <span>Unit Price:</span>
                          <span className="font-semibold">₹{selectedCommitment.silatItem?.price}</span>
                        </div>

                        <div className="flex justify-between">
                          <span>Contribution:</span>
                          <span className="font-semibold">
                            {selectedCommitment.contributionType === 'cash' ? 'Price (Cash Equivalent)' : 'In Kind'}
                          </span>
                        </div>

                        <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
                          <span>Total:</span>
                          <span className="font-bold text-green-700">
                            ₹{(quantity || selectedCommitment.quantity) * selectedCommitment.silatItem?.price}
                            {console.log("qty: ", quantity || selectedCommitment.quantity, " quantity type: ", typeof(quantity), " price: ", selectedCommitment.silatItem?.price, " price type: ", typeof(selectedCommitment.silatItem?.price))}
                          </span>
                        </div>
                      </div>
                    </div>


                    </>
                  ) : (
                    <>
                      {/* Zabihat Fields */}
                      <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Item Name
                        </label>
                        <p className="text-fmb font-semibold flex items-center text-lg">
                          <GiSheep size={18} className="mr-2 text-gray-600" />
                          Zabihat
                        </p>
                      </div>
              
                      <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-600 mb-2">
                          Quantity
                        </label>
                        {!isEditingQty ? (
                          <div className="flex items-center gap-2">
                            <p className="text-gray-800 font-semibold text-lg">
                              {selectedCommitment.quantity}
                            </p>
                            {canEdit && (
                              <button
                                onClick={() => setIsEditingQty(true)}
                                className="text-blue-600 text-sm font-medium hover:underline"
                              >
                                Edit Qty
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-start gap-2">
                            <input
                              type="number"
                              disabled={updatingQty}
                              placeholder={selectedCommitment.quantity}
                              onChange={(e) => setQuantity(e.target.value)}
                              className="w-24 border rounded-lg p-1 text-right"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                onClick={handleUpdate}
                                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingQty(false);
                                  setQuantity(selectedCommitment.quantity);
                                }}
                                className="px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Price per Unit
                        </label>
                        <p className="text-gray-800 font-semibold text-lg">
                          {formatCurrency(selectedCommitment.silatZabihat?.price || 0)}
                        </p>
                      </div>

                      <div className="p-4 rounded-2xl shadow-sm border border-gray-200 bg-gray-50">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Total Amount
                        </label>
                        <p className="font-semibold text-xl text-green-700">
                          {formatCurrency(
                            (quantity || selectedCommitment.quantity) * (selectedCommitment.silatZabihat?.price || 0)
                          )}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Location & Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  {selectedCommitment.individualName ? (
                    <>
                      <h3 className="text-lg font-semibold text-fmb mb-3">Individual Name</h3>
                      <div className="space-y-2">
                        <span className="text-gray-700">{selectedCommitment.individualName}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold text-fmb mb-3">Location</h3>
                      <div className="space-y-2">
                        <div className="flex items-center text-gray-700">
                          <FiMapPin size={16} className="mr-2" />
                          <span>
                            {selectedCommitment.jamiat?.jamiat_name || ""}
                            {selectedCommitment.jamiat?.jamaat_name
                              ? ` - ${selectedCommitment.jamiat.jamaat_name}`
                              : ""}
                          </span>
                        </div>
                        {selectedCommitment.volunteerName && (
                          <div className="flex items-center text-gray-700">
                            <span className="font-medium w-20">Name:</span>
                            <span>{selectedCommitment.volunteerName}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              


                <div>
                  <h3 className="text-lg font-semibold text-fmb mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-700">
                      <span className="font-medium w-20">Name:</span>
                      <span>{selectedCommitment.contactName}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <FiPhone size={16} className="mr-2" />
                      <span>{selectedCommitment.contactNumber}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <FiMail size={16} className="mr-2" />
                      <span>{selectedCommitment.contactEmail}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status & Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-fmb mb-3">Status</h3>
                  <div className={`px-4 py-2 rounded-lg border flex items-center gap-2 w-fit ${getStatusColor(selectedCommitment.status)}`}>
                    {getStatusIcon(selectedCommitment.status)}
                    <span className="font-medium">
                      {selectedCommitment.status.charAt(0).toUpperCase() + selectedCommitment.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-fmb mb-3">
                    {activeTab === 'items' ? 'Delivery Information' : 'Payment Information'}
                  </h3>
                  <div className="space-y-2">
                    {activeTab === 'items' ? (
                      <>
                        <div className="flex items-center text-gray-700">
                          <FiCalendar size={16} className="mr-2" />
                          <span className="font-medium">Expected: </span>
                          <span className={isOverdue(selectedCommitment.estimatedDeliveryDate, selectedCommitment.status) ? 'text-red-600 font-medium' : ''}>
                            {formatDate(selectedCommitment.estimatedDeliveryDate)}
                            {isOverdue(selectedCommitment.estimatedDeliveryDate, selectedCommitment.status) && ' (Overdue)'}
                          </span>
                        </div>
                        {selectedCommitment.deliveryDate && (
                          <div className="flex items-center text-gray-700">
                            <FiCheckCircle size={16} className="mr-2 text-green-500" />
                            <span className="font-medium">Delivered: </span>
                            <span className="text-green-600">{formatDate(selectedCommitment.deliveryDate)}</span>
                          </div>
                        )}
                        {selectedCommitment.ewayBill && (
                          <div className="flex items-center text-gray-700">
                            <FiTruck size={16} className="mr-2" />
                            <span className="font-medium">E-Way Bill: </span>
                            <span>{selectedCommitment.ewayBill}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex items-center text-gray-700">
                          <FiCalendar size={16} className="mr-2" />
                          <span className="font-medium">Payment Due: </span>
                          <span className={isOverdue(selectedCommitment.estimatedDeliveryDate, selectedCommitment.status) ? 'text-red-600 font-medium' : ''}>
                            {formatDate(selectedCommitment.estimatedDeliveryDate)}
                            {isOverdue(selectedCommitment.estimatedDeliveryDate, selectedCommitment.status) && ' (Overdue)'}
                          </span>
                        </div>
                        {selectedCommitment.paymentDate && (
                          <div className="flex items-center text-gray-700">
                            <FiCheckCircle size={16} className="mr-2 text-green-500" />
                            <span className="font-medium">Paid on: </span>
                            <span className="text-green-600">{formatDate(selectedCommitment.paymentDate)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-fmb mb-3">Timeline</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Created: </span>
                    <span className="text-gray-600">{formatDate(selectedCommitment.createdAt)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Last Updated: </span>
                    <span className="text-gray-600">{formatDate(selectedCommitment.updatedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {/* Modal Actions: Approve + Delete (far left, admin/edit-only) and Close (far right) */}
              <div className={`flex mt-6 ${(canEdit || canDelete) ? 'justify-between' : 'justify-end'}`}>
                <div className="flex gap-3">
                  {canEdit && (
                    <button
                      onClick={handleCommitmentStatusUpdate}
                      disabled={['received','partial-received'].includes(selectedCommitment.status)}
                      className={`px-6 py-2 rounded-lg transition-colors ${
                        ['received','partial-received'].includes(selectedCommitment.status)
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                      title={['received','partial-received'].includes(selectedCommitment.status) ? 'Commitment already approved / in receiving process' : 'Approve this commitment'}
                    >
                      Approve
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={handleDeleteCommitment}
                      disabled={deletingCommitment}
                      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete this commitment"
                    >
                      {deletingCommitment ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-6 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 transition-colors"
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
      {/* toast */}
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}
    </div>
  );
};

export default CommitmentsDisplay;